/**
 * Branch create/edit form component
 */
import { useState, useEffect, useMemo } from 'react';
import { Branch } from '@/types';
import { CreateBranchInput, UpdateBranchInput, useBranch } from '@/contexts/BranchContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { toast } from '@/lib/toast';

interface BranchFormProps {
  branch?: Branch;
  franchiseId: string;
  onSubmit: (data: CreateBranchInput | UpdateBranchInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

// Generate a temporary branch ID for new branches
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export function BranchForm({ branch, franchiseId, onSubmit, onCancel, isLoading }: BranchFormProps) {
  const { uploadPhoto } = useBranch();
  const [uploading, setUploading] = useState(false);

  // Generate a temporary ID for new branches to use for image uploads
  const tempBranchId = useMemo(() => branch?.branchId || generateTempId(), [branch?.branchId]);
  const [formData, setFormData] = useState<CreateBranchInput>({
    franchiseId,
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Spain',
    phone: '',
    email: '',
    photo: undefined,
    schedule: {
      monday: { isOpen: true, open: '09:00', close: '20:00' },
      tuesday: { isOpen: true, open: '09:00', close: '20:00' },
      wednesday: { isOpen: true, open: '09:00', close: '20:00' },
      thursday: { isOpen: true, open: '09:00', close: '20:00' },
      friday: { isOpen: true, open: '09:00', close: '20:00' },
      saturday: { isOpen: true, open: '10:00', close: '18:00' },
      sunday: { isOpen: false, open: '', close: '' },
    },
    isActive: true,
  });

  useEffect(() => {
    if (branch) {
      setFormData({
        franchiseId: branch.franchiseId,
        name: branch.name,
        address: branch.address,
        city: branch.city,
        province: branch.province,
        postalCode: branch.postalCode,
        country: branch.country,
        phone: branch.phone,
        email: branch.email || '',
        photo: branch.photo,
        schedule: branch.schedule,
        isActive: branch.isActive,
      });
    }
  }, [branch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const handlePhotoUpload = async (file: File) => {
    setUploading(true);
    try {
      // Use the generated temp ID for new branches or the real ID for existing ones
      const url = await uploadPhoto(tempBranchId, file);
      setFormData(prev => ({ ...prev, photo: url }));
    } catch (error) {
      console.error('Error uploading photo:', error);
      // Show error to user
      toast.error('Error al subir la foto. Por favor, intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const handleScheduleChange = (day: typeof DAYS[number], field: 'isOpen' | 'open' | 'close', value: boolean | string) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value,
        },
      },
    }));
  };

  return (
    <Card className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-ink">
        {branch ? '✏️ Editar Sucursal' : '➕ Nueva Sucursal'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">
            📸 Foto de la Sucursal
          </label>
          <ImageUpload
            currentImage={formData.photo}
            onUpload={handlePhotoUpload}
            uploading={uploading}
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-ink-muted mb-2">
            Nombre de la Sucursal *
          </label>
          <input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder="San Jorge"
          />
        </div>

        {/* Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-ink-muted mb-2">
              📍 Dirección *
            </label>
            <input
              id="address"
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="Calle San Jorge 123"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-ink-muted mb-2">
              🏙️ Ciudad *
            </label>
            <input
              id="city"
              type="text"
              required
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="Pamplona"
            />
          </div>

          <div>
            <label htmlFor="province" className="block text-sm font-medium text-ink-muted mb-2">
              🗺️ Provincia *
            </label>
            <input
              id="province"
              type="text"
              required
              value={formData.province}
              onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="Navarra"
            />
          </div>

          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-ink-muted mb-2">
              📮 Código Postal *
            </label>
            <input
              id="postalCode"
              type="text"
              required
              value={formData.postalCode}
              onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="31001"
            />
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-ink-muted mb-2">
              🌍 País *
            </label>
            <input
              id="country"
              type="text"
              required
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="España"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-ink-muted mb-2">
              📞 Teléfono *
            </label>
            <input
              id="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="+34 948 222 333"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-2">
              ✉️ Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="sucursal@ejemplo.com"
            />
          </div>
        </div>

        {/* Schedule */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-4">📅 Horario</label>
          <div className="space-y-3">
            {DAYS.map((day) => (
              <div key={day} className="flex items-center gap-4 p-3 bg-surface-2 rounded-lg">
                <div className="w-24">
                  <input
                    type="checkbox"
                    id={`${day}-open`}
                    checked={formData.schedule[day].isOpen}
                    onChange={(e) => handleScheduleChange(day, 'isOpen', e.target.checked)}
                    className="w-4 h-4 text-info border-hairline rounded focus:ring-focus mr-2"
                  />
                  <label htmlFor={`${day}-open`} className="text-sm font-medium capitalize text-ink">
                    {day}
                  </label>
                </div>

                {formData.schedule[day].isOpen && (
                  <>
                    <div>
                      <label htmlFor={`${day}-open-time`} className="text-xs text-ink-muted">Abre</label>
                      <input
                        id={`${day}-open-time`}
                        type="time"
                        value={formData.schedule[day].open}
                        onChange={(e) => handleScheduleChange(day, 'open', e.target.value)}
                        className="block px-2 py-1 border border-hairline rounded text-sm bg-surface text-ink"
                      />
                    </div>
                    <div>
                      <label htmlFor={`${day}-close-time`} className="text-xs text-ink-muted">Cierra</label>
                      <input
                        id={`${day}-close-time`}
                        type="time"
                        value={formData.schedule[day].close}
                        onChange={(e) => handleScheduleChange(day, 'close', e.target.value)}
                        className="block px-2 py-1 border border-hairline rounded text-sm bg-surface text-ink"
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Is Active */}
        <div className="flex items-center">
          <input
            id="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 text-info border-hairline rounded focus:ring-focus"
          />
          <label htmlFor="isActive" className="ml-2 text-sm font-medium text-ink-muted">
            ✅ Activa
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" isLoading={isLoading} className="flex-1">
            {branch ? '💾 Actualizar Sucursal' : '➕ Crear Sucursal'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading} className="flex-1">
            ❌ Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
